/**
 * MaintenanceGuard - Wrapper that redirects to /maintenance when maintenance mode is on
 * 
 * IMPORTANT: Does NOT block /admin or /maintenance routes
 * This version works without useLocation by checking window.location
 */
import { useAppControl } from '@/hooks/useAppControl';
import { Loader2 } from 'lucide-react';

interface MaintenanceGuardProps {
  children: React.ReactNode;
}

// Routes that should never be blocked by maintenance mode
const EXEMPT_ROUTES = ['/admin', '/maintenance'];

export function MaintenanceGuard({ children }: MaintenanceGuardProps) {
  const { data: appControl, isLoading } = useAppControl();

  // Check if current route is exempt using window.location
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const isExempt = EXEMPT_ROUTES.some(route => 
    pathname === route || pathname.startsWith(route + '/')
  );

  // If exempt, always render children
  if (isExempt) {
    return <>{children}</>;
  }

  // Show loading briefly while checking maintenance status
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // If maintenance mode is enabled, redirect to maintenance page
  if (appControl?.maintenance_enabled) {
    // Use window.location for redirect since we're outside Router context
    window.location.href = '/maintenance';
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Otherwise, render children normally
  return <>{children}</>;
}
